<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, ref, watch } from 'vue';
import LaisModal from 'dashboard/components-next/LaisModal.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';

const props = defineProps({
  show: { type: Boolean, default: false },
  activeVisit: { type: Object, default: () => ({}) },
  quickVisitDateText: { type: String, default: '—' },
  hasVisitorCheckIn: { type: Boolean, default: false },
});

const emit = defineEmits(['close', 'reschedule', 'continue']);

const reason = ref('');
const errorMessage = ref('');
const trimmedReason = computed(() => reason.value.trim());
const canContinue = computed(() => trimmedReason.value.length >= 3);

const submit = () => {
  if (!canContinue.value) {
    errorMessage.value = 'Informe o motivo do cancelamento.';
    return;
  }

  emit('continue', {
    reason: 'OTHER',
    responsible: 'AGENT',
    reasonMessage: trimmedReason.value,
  });
};

watch(
  () => props.show,
  isOpen => {
    if (isOpen) {
      reason.value = '';
      errorMessage.value = '';
    }
  }
);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <LaisModal
    :show="show"
    title="Você tem certeza que deseja cancelar?"
    max-width-class="max-w-[520px]"
    @close="emit('close')"
  >
    <div class="grid gap-4 text-[14px] leading-relaxed text-lais-text-body">
      <p>
        Não deixe o cliente sem uma visita marcada, reagende a visita para
        mantê-lo engajado.
      </p>
      <div
        class="rounded-lais-sm border border-lais-border bg-lais-bg-soft px-4 py-3 text-[13px]"
      >
        <p class="flex min-w-0 items-center gap-1 font-semibold text-lais-text">
          <span class="truncate">
            {{ activeVisit?.contact?.name || 'Cliente não informado' }}
          </span>
          <span
            v-if="hasVisitorCheckIn"
            class="i-lucide-circle-check size-4 shrink-0 text-emerald-700"
            title="Check-in confirmado"
            aria-label="Check-in confirmado"
          />
        </p>
        <p class="mt-1 text-lais-text-muted">{{ quickVisitDateText }}</p>
      </div>

      <label class="grid gap-1">
        <span class="font-semibold text-lais-text">
          Motivo do cancelamento *
        </span>
        <textarea
          v-model="reason"
          rows="3"
          class="rounded-lais-sm border border-lais-border bg-white px-3 py-2 text-[14px] text-lais-text outline-none transition focus:border-lais-primary focus:shadow-lais-focus"
          placeholder="Ex: cliente pediu para cancelar"
        />
      </label>

      <p
        v-if="errorMessage"
        class="rounded-lais-sm border border-lais-danger/30 bg-lais-danger/10 px-3 py-2 text-[13px] font-semibold text-lais-danger"
      >
        {{ errorMessage }}
      </p>
    </div>
    <template #footer>
      <div class="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <LaisPrimaryButton variant="ghost" @click="emit('close')">
          Voltar
        </LaisPrimaryButton>
        <LaisPrimaryButton
          variant="ghost"
          icon="i-lucide-calendar-clock"
          @click="emit('reschedule')"
        >
          Reagendar
        </LaisPrimaryButton>
        <LaisPrimaryButton
          variant="primary"
          icon="i-lucide-arrow-right"
          :disabled="!canContinue"
          @click="submit"
        >
          Continuar
        </LaisPrimaryButton>
      </div>
    </template>
  </LaisModal>
</template>
